<template>
  <b-container fluid>
    <admin-breadcrumb name="Generate Marketing Email"/>
    <b-row>
      <b-col cols="12">
        <admin-panel
            title="Create HubSpot Marketing Email Draft"
            sub-title="Fill in the draft details, generate the storefront HTML, and send the complete payload to the GreenLists HubSpot API."
            with-footer
            :second-body="!!submissionResult"
        >
          <b-form @submit.prevent="submitDraft">
            <b-row>
              <b-col md="6">
                <b-form-group
                    label-for="marketing-email-name"
                    :invalid-feedback="validation.name"
                >
                  <template #label>
                    Draft Name <span class="required-asterisk">*</span>
                  </template>
                  <b-form-input
                      id="marketing-email-name"
                      v-model="form.name"
                      :state="fieldState('name')"
                      placeholder="Storefronts April 2026 Draft"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label-for="marketing-email-subject"
                    :invalid-feedback="validation.subject"
                >
                  <template #label>
                    Subject <span class="required-asterisk">*</span>
                  </template>
                  <b-form-input
                      id="marketing-email-subject"
                      v-model="form.subject"
                      :state="fieldState('subject')"
                      placeholder="Explore New Storefronts on GreenLists"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Email Title"
                    label-for="marketing-email-title"
                    :description="'Used when generating the storefront email HTML.'"
                    :invalid-feedback="validation.title"
                >
                  <b-form-input
                      id="marketing-email-title"
                      v-model="form.title"
                      :state="fieldState('title')"
                      placeholder="Shop By Store"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Preview Text"
                    label-for="marketing-email-preview-text"
                    :invalid-feedback="validation.previewText"
                >
                  <b-form-input
                      id="marketing-email-preview-text"
                      v-model="form.previewText"
                      :state="fieldState('previewText')"
                      placeholder="Browse the latest storefronts on GreenLists"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="From Name"
                    label-for="marketing-email-from-name"
                    :invalid-feedback="validation.fromName"
                >
                  <b-form-input
                      id="marketing-email-from-name"
                      v-model="form.fromName"
                      :state="fieldState('fromName')"
                      placeholder="GreenLists"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Reply-To"
                    label-for="marketing-email-reply-to"
                    :invalid-feedback="validation.replyTo"
                >
                  <b-form-input
                      id="marketing-email-reply-to"
                      v-model="form.replyTo"
                      :state="fieldState('replyTo')"
                      placeholder="support@greenlists.com"
                      type="email"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Custom Reply-To"
                    label-for="marketing-email-custom-reply-to"
                    :invalid-feedback="validation.customReplyTo"
                >
                  <b-form-input
                      id="marketing-email-custom-reply-to"
                      v-model="form.customReplyTo"
                      :state="fieldState('customReplyTo')"
                      placeholder="marketing@greenlists.com"
                      type="email"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Language"
                    label-for="marketing-email-language"
                    :invalid-feedback="validation.language"
                >
                  <b-form-input
                      id="marketing-email-language"
                      v-model="form.language"
                      :state="fieldState('language')"
                      placeholder="en"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Active Domain"
                    label-for="marketing-email-active-domain"
                    :invalid-feedback="validation.activeDomain"
                >
                  <b-form-input
                      id="marketing-email-active-domain"
                      v-model="form.activeDomain"
                      :state="fieldState('activeDomain')"
                      placeholder="info.greenlists.com"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Campaign"
                    label-for="marketing-email-campaign"
                    :invalid-feedback="validation.campaign"
                >
                  <b-form-input
                      id="marketing-email-campaign"
                      v-model="form.campaign"
                      :state="fieldState('campaign')"
                      placeholder="HubSpot campaign id"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Folder ID"
                    label-for="marketing-email-folder-id"
                    :invalid-feedback="validation.folderIdV2"
                >
                  <b-form-input
                      id="marketing-email-folder-id"
                      v-model.number="form.folderIdV2"
                      :state="fieldState('folderIdV2')"
                      type="number"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Business Unit ID"
                    label-for="marketing-email-business-unit-id"
                    :invalid-feedback="validation.businessUnitId"
                >
                  <b-form-input
                      id="marketing-email-business-unit-id"
                      v-model.number="form.businessUnitId"
                      :state="fieldState('businessUnitId')"
                      type="number"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Office Location ID"
                    label-for="marketing-email-office-location-id"
                    :invalid-feedback="validation.officeLocationId"
                >
                  <b-form-input
                      id="marketing-email-office-location-id"
                      v-model="form.officeLocationId"
                      :state="fieldState('officeLocationId')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Preferences Group ID"
                    label-for="marketing-email-preferences-group-id"
                    :invalid-feedback="validation.preferencesGroupId"
                >
                  <b-form-input
                      id="marketing-email-preferences-group-id"
                      v-model="form.preferencesGroupId"
                      :state="fieldState('preferencesGroupId')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group
                    label="Subscription ID"
                    label-for="marketing-email-subscription-id"
                    :invalid-feedback="validation.subscriptionId"
                >
                  <b-form-input
                      id="marketing-email-subscription-id"
                      v-model="form.subscriptionId"
                      :state="fieldState('subscriptionId')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                    label="Subscription Name"
                    label-for="marketing-email-subscription-name"
                    :invalid-feedback="validation.subscriptionName"
                >
                  <b-form-input
                      id="marketing-email-subscription-name"
                      v-model="form.subscriptionName"
                      :state="fieldState('subscriptionName')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group
                label="Plain Text Version"
                label-for="marketing-email-plain-text"
                :description="'Optional. Leave blank to auto-generate from the storefront HTML.'"
                :invalid-feedback="validation.plainTextVersion"
            >
              <b-form-textarea
                  id="marketing-email-plain-text"
                  v-model="form.plainTextVersion"
                  :state="fieldState('plainTextVersion')"
                  rows="4"
              />
            </b-form-group>
          </b-form>

          <template v-slot:footer>
            <b-btn
                variant="primary"
                :disabled="formState.processing"
                @click.prevent="submitDraft"
            >
              Submit
              <b-spinner v-if="formState.processing" class="ml-1" small/>
            </b-btn>
          </template>

          <template v-slot:second-body>
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h5 class="mb-1">Submission Result</h5>
                <p class="text-muted mb-0">
                  HubSpot response and generated HTML preview from the latest submission.
                </p>
              </div>
              <b-badge variant="success">Draft Created</b-badge>
            </div>

            <b-row>
              <b-col lg="5" class="mb-3 mb-lg-0">
                <b-card bg-variant="light" class="h-100">
                  <h6 class="text-uppercase text-muted mb-3">HubSpot Response</h6>
                  <pre class="mb-0 small marketing-email-response">{{ formattedResult }}</pre>
                </b-card>
              </b-col>
              <b-col lg="7">
                <b-card bg-variant="light" class="h-100">
                  <h6 class="text-uppercase text-muted mb-3">Generated HTML Preview</h6>
                  <div class="marketing-email-preview" v-html="submissionResult.html"/>
                </b-card>
              </b-col>
            </b-row>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";

export default {
  name: "AdminGenerateMarketingEmail",
  components: {AdminPanel, AdminBreadcrumb},
  data() {
    return {
      form: {
        name: '',
        subject: '',
        title: 'Shop By Store',
        previewText: '',
        plainTextVersion: '',
        fromName: '',
        replyTo: '',
        customReplyTo: '',
        activeDomain: '',
        campaign: '',
        folderIdV2: null,
        businessUnitId: null,
        language: 'en',
        officeLocationId: '',
        preferencesGroupId: '',
        subscriptionId: '',
        subscriptionName: ''
      },
      formState: {
        processing: false
      },
      validation: {},
      submissionResult: null
    };
  },
  computed: {
    formattedResult() {
      return this.submissionResult ? JSON.stringify(this.submissionResult.hubspot, null, 2) : '';
    }
  },
  methods: {
    fieldState(fieldName) {
      if (typeof this.validation[fieldName] === 'undefined') {
        return null;
      }

      return false;
    },
    buildPayload() {
      const payload = {};

      Object.keys(this.form).forEach((fieldName) => {
        const value = this.form[fieldName];

        if (value === '' || value === null || typeof value === 'undefined') {
          return;
        }

        payload[fieldName] = value;
      });

      return payload;
    },
    submitDraft() {
      this.formState.processing = true;
      this.validation = {};

      this.$api.post(API_ROUTES.ADMIN_GENERATE_MARKETING_EMAIL, this.buildPayload()).then(({data}) => {
        this.submissionResult = data;
        this.$bvToast.toast('Marketing email draft created successfully in HubSpot.', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response}) => {
        const data = response?.data || {};
        const validationErrors = {};

        if (typeof data.errors !== 'undefined') {
          Object.keys(data.errors).forEach((fieldName) => {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          });
        }

        this.validation = validationErrors;
        this.$bvToast.toast(data.message ?? 'Failed to create marketing email draft.', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  }
}
</script>

<style scoped>
.required-asterisk {
  color: #dc3545;
}

.marketing-email-response {
  max-height: 480px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.marketing-email-preview {
  max-height: 480px;
  overflow: auto;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 1rem;
}
</style>
